/**
 * Countdown Stack Component
 * Displays built-in and custom countdown timers with quick actions.
 */
export class CountdownStack {
  constructor(container, dataManager, options = {}) {
    this.container = container;
    this.data = dataManager;
    this.analytics = options.analytics || null;
    this.getBuiltinEntries = options.getBuiltinEntries || (() => []);
    this.getCalendarEvents = options.getCalendarEvents || (() => []);
    this.onRefreshRequested = options.onRefreshRequested || (() => {});

    if (!this.container || !this.data) {
      console.warn('[CountdownStack] Missing container or data manager');
      return;
    }

    this.unsubscribeFns = [];

    this.render();
    this.subscribe();
  }

  subscribe() {
    if (typeof this.data.subscribe !== 'function') {
      return;
    }

    const subscribe = (key, handler) => {
      try {
        const unsub = this.data.subscribe(key, handler);
        if (typeof unsub === 'function') {
          this.unsubscribeFns.push(unsub);
        }
      } catch (error) {
        console.warn('[CountdownStack] Failed to subscribe to', key, error);
      }
    };

    subscribe('countdowns', () => this.refresh());
    subscribe('calendar_events', () => this.refresh());
  }

  dispose() {
    this.unsubscribeFns.forEach((fn) => {
      try {
        fn();
      } catch (error) {
        console.warn('[CountdownStack] Failed to unsubscribe', error);
      }
    });
    this.unsubscribeFns = [];
    this.container.innerHTML = '';
  }

  render() {
    this.container.innerHTML = `
      <div class="card card--nature countdown-stack-card">
        <div class="card__header">
          <h3 class="card__title">
            <span class="emoji">⏳</span>
            Smart Countdowns
          </h3>
          <button class="btn btn--small countdown-add-btn" id="countdown-add-btn">Add countdown</button>
        </div>
        <div class="card__content">
          <div class="countdown-stack__list" id="countdown-list"></div>
          <div class="countdown-stack__empty" id="countdown-empty" style="display: none;">
            <p>No countdowns yet. Add the routines or events you want to stay ahead of.</p>
          </div>
        </div>
      </div>
    `;

    this.listEl = this.container.querySelector('#countdown-list');
    this.emptyStateEl = this.container.querySelector('#countdown-empty');

    this.container
      .querySelector('#countdown-add-btn')
      ?.addEventListener('click', () => this.openCustomCountdownModal());

    this.refresh();
  }

  refresh() {
    const builtin = this.getBuiltinEntries?.() || [];
    const custom = this.transformCustomCountdowns();
    const entries = [...builtin, ...custom].filter(Boolean);
    this.renderEntries(entries);
  }

  renderEntries(entries) {
    if (!entries.length) {
      this.emptyStateEl.style.display = 'block';
      this.listEl.innerHTML = '';
      return;
    }

    this.emptyStateEl.style.display = 'none';

    const markup = entries
      .sort((a, b) => (a.sortWeight ?? 0) - (b.sortWeight ?? 0))
      .map((entry) => this.renderEntry(entry))
      .join('');

    this.listEl.innerHTML = markup;

    this.listEl.querySelectorAll('[data-action="primary"]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.id;
        const entry = entries.find((item) => item.id === id);
        entry?.onPrimary?.();
      });
    });

    this.listEl.querySelectorAll('[data-action="secondary"]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.id;
        const entry = entries.find((item) => item.id === id);
        entry?.onSecondary?.();
      });
    });

    this.listEl.querySelectorAll('[data-action="edit"]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.id;
        const countdown = this.getEditableCountdowns().find((item) => item.id === id);
        if (countdown) {
          this.openCustomCountdownModal(countdown);
        }
      });
    });

    this.listEl.querySelectorAll('[data-action="delete"]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.id;
        this.deleteCustomCountdown(id);
      });
    });
  }

  renderEntry(entry) {
    const statusClass = entry.status ? `countdown-entry--${entry.status}` : '';
    const badge = entry.badge ? `<span class="countdown-entry__badge">${entry.badge}</span>` : '';
    const primaryAction = entry.primaryActionLabel
      ? `<button class="btn btn--tiny countdown-entry__cta" data-action="primary" data-id="${entry.id}">${entry.primaryActionLabel}</button>`
      : '';
    const secondaryAction = entry.secondaryActionLabel
      ? `<button class="btn btn--tiny countdown-entry__cta btn--ghost" data-action="secondary" data-id="${entry.id}">${entry.secondaryActionLabel}</button>`
      : '';
    const quickMenu = entry.isEditable
      ? `<div class="countdown-entry__menu">
           <button class="countdown-entry__menu-btn" data-action="edit" data-id="${entry.id}" title="Edit countdown">✎</button>
           <button class="countdown-entry__menu-btn" data-action="delete" data-id="${entry.id}" title="Remove countdown">×</button>
         </div>`
      : '';

    return `
      <div class="countdown-entry ${statusClass}" data-id="${entry.id}">
        <div class="countdown-entry__info">
          <div class="countdown-entry__label">
            ${badge}
            <span>${entry.label}</span>
          </div>
          <div class="countdown-entry__primary">${entry.primaryText || '--'}</div>
          ${entry.secondaryText ? `<div class="countdown-entry__secondary">${entry.secondaryText}</div>` : ''}
        </div>
        <div class="countdown-entry__actions">
          ${primaryAction}
          ${secondaryAction}
          ${quickMenu}
        </div>
      </div>
    `;
  }

  transformCustomCountdowns() {
    const now = Date.now();
    const events = this.getCalendarEvents?.() || [];
    const mapEventById = new Map(events.filter((event) => event?.id).map((event) => [event.id, event]));

    return this.getEditableCountdowns()
      .map((countdown, index) => {
        let baseDate = countdown.target ? new Date(countdown.target) : null;
        if ((!baseDate || Number.isNaN(baseDate.getTime())) && countdown.source === 'calendar' && countdown.calendarEventId) {
          const event = mapEventById.get(countdown.calendarEventId);
          if (event?.start) {
            baseDate = new Date(event.start);
          }
        }

        if (!baseDate || Number.isNaN(baseDate.getTime())) {
          return null;
        }

        const offsetMinutes = Number(countdown.offsetMinutes) || 0;
        const isLeaveMode = countdown.mode === 'leaveBy';
        const targetDate = isLeaveMode
          ? new Date(baseDate.getTime() - offsetMinutes * 60000)
          : baseDate;

        const diff = targetDate.getTime() - now;
        const status = this.statusForDiff(diff);

        const secondary = isLeaveMode && offsetMinutes > 0
          ? `${this.formatTimeLabel(baseDate)} · leave ${offsetMinutes} min early`
          : this.formatTimeLabel(baseDate);

        const badge = countdown.category === 'appointment' ? 'APPT' : 'CUSTOM';

        return {
          id: countdown.id,
          label: countdown.label || 'Countdown',
          badge,
          status,
          primaryText: this.formatDiff(diff),
          secondaryText: secondary,
          primaryActionLabel: null,
          sortWeight: 100 + index,
          isEditable: true,
        };
      })
      .filter(Boolean);
  }

  getEditableCountdowns() {
    const payload = this.getAllCountdowns();
    return payload.filter((item) => item && item.source !== 'system');
  }

  getAllCountdowns() {
    try {
      const payload = this.data.get('countdowns', []);
      return Array.isArray(payload) ? payload.map((item) => ({ ...item })) : [];
    } catch (error) {
      console.warn('[CountdownStack] Failed to read countdowns', error);
      return [];
    }
  }

  normalizeCountdownForStorage(countdown, current = null) {
    if (!countdown) {
      return null;
    }

    const now = new Date().toISOString();
    const id =
      typeof countdown.id === 'string' && countdown.id.trim()
        ? countdown.id.trim()
        : current?.id || this.generateId();
    const label =
      typeof countdown.label === 'string' && countdown.label.trim()
        ? countdown.label.trim()
        : current?.label || 'Countdown';

    let mode = countdown.mode;
    if (mode === 'leave') mode = 'leaveBy';
    if (mode === 'event') mode = 'eventStart';
    if (mode !== 'leaveBy' && mode !== 'eventStart') {
      mode = current?.mode || 'eventStart';
    }

    const offsetMinutes = Number.isFinite(Number(countdown.offsetMinutes))
      ? Math.max(0, Number(countdown.offsetMinutes))
      : Number.isFinite(Number(current?.offsetMinutes))
      ? Math.max(0, Number(current.offsetMinutes))
      : 0;

    let target = countdown.target || countdown.targetDateTime || current?.target || current?.targetDateTime || null;
    if (target) {
      const targetDate = new Date(target);
      target = Number.isNaN(targetDate.getTime()) ? null : targetDate.toISOString();
    }

    const calendarEventId = countdown.calendarEventId
      ? String(countdown.calendarEventId)
      : current?.calendarEventId || null;

    let category = countdown.category || countdown.type || current?.category || current?.type || 'custom';
    if (!category) {
      category = calendarEventId ? 'appointment' : 'custom';
    }

    const source = countdown.source || current?.source || (calendarEventId ? 'calendar' : 'manual');

    const existingMetadata =
      (current?.metadata && typeof current.metadata === 'object') ||
      (countdown.metadata && typeof countdown.metadata === 'object')
        ? { ...(current?.metadata || {}), ...(countdown.metadata || {}) }
        : {};

    const createdAt = current?.createdAt || existingMetadata.createdAt || now;
    const updatedAt = countdown.updatedAt || now;

    return {
      id,
      label,
      category,
      type: category,
      source,
      mode,
      offsetMinutes,
      target,
      targetDateTime: target,
      calendarEventId,
      active: countdown.active !== false,
      metadata: { ...existingMetadata, createdAt, updatedAt },
      createdAt,
      updatedAt,
    };
  }

  saveCustomCountdown(countdown) {
    const list = this.getAllCountdowns();
    const existingIndex = list.findIndex((item) => item.id === countdown.id);
    const existing = existingIndex >= 0 ? list[existingIndex] : null;
    const normalized = this.normalizeCountdownForStorage(countdown, existing);
    if (!normalized) {
      return;
    }

    if (existingIndex >= 0) {
      list[existingIndex] = { ...existing, ...normalized };
    } else {
      list.push(normalized);
    }

    this.data.set('countdowns', list);
    this.analytics?.track?.('countdown_custom_saved', { source: normalized.source || 'manual' });
    this.onRefreshRequested?.();
  }

  deleteCustomCountdown(countdownId) {
    const list = this.getAllCountdowns().filter((item) => item?.id !== countdownId);
    this.data.set('countdowns', list);
    this.analytics?.track?.('countdown_custom_deleted');
    this.onRefreshRequested?.();
  }

  openCustomCountdownModal(countdown = null) {
    const calendarEvents = this.getCalendarEvents?.() || [];
    const nowISO = new Date().toISOString().slice(0, 16);
    const defaultTarget = countdown?.target || countdown?.targetDateTime || `${nowISO}:00`;

    const existing = {
      id: countdown?.id || this.generateId(),
      label: countdown?.label || '',
      category: countdown?.category || countdown?.type || 'custom',
      source: countdown?.source || (countdown?.calendarEventId ? 'calendar' : 'manual'),
      mode: countdown?.mode || 'eventStart',
      offsetMinutes: Number(countdown?.offsetMinutes) || 0,
      calendarEventId: countdown?.calendarEventId || '',
      target: defaultTarget,
      metadata: countdown?.metadata || {},
    };

    const modal = document.createElement('div');
    modal.className = 'countdown-modal';
    modal.innerHTML = `
      <div class="countdown-modal__backdrop" data-action="close"></div>
      <div class="countdown-modal__dialog" role="dialog" aria-modal="true">
        <header class="countdown-modal__header">
          <h3>${countdown ? 'Edit countdown' : 'Add countdown'}</h3>
          <button class="countdown-modal__close" data-action="close" aria-label="Close">×</button>
        </header>
        <div class="countdown-modal__body">
          <label for="countdown-label">Label</label>
          <input id="countdown-label" type="text" value="${this.escapeHtml(existing.label)}" placeholder="e.g., Leave for yoga" />

          <label for="countdown-type">Type</label>
          <select id="countdown-type">
            <option value="custom" ${existing.category === 'custom' ? 'selected' : ''}>Custom</option>
            <option value="appointment" ${existing.category === 'appointment' ? 'selected' : ''}>Appointment</option>
          </select>

          ${
            calendarEvents.length
              ? `
          <label for="countdown-event">Link to calendar event</label>
          <select id="countdown-event">
            <option value="">-- No event --</option>
            ${calendarEvents
              .map((event) => {
                const start = event.start ? new Date(event.start) : null;
                const label = `${event.summary || 'Untitled'} · ${start ? this.formatTimeLabel(start) : ''}`;
                const selected = existing.calendarEventId === event.id ? 'selected' : '';
                return `<option value="${this.escapeHtmlAttribute(event.id || '')}" ${selected}>${this.escapeHtml(
                  label
                )}</option>`;
              })
              .join('')}
          </select>
        `
              : ''
          }

          <label for="countdown-date">Event date</label>
          <input id="countdown-date" type="date" />

          <label for="countdown-time">Event time</label>
          <input id="countdown-time" type="time" />

          <label>Countdown mode</label>
          <div class="countdown-modal__modes">
            <label>
              <input type="radio" name="countdown-mode" value="eventStart" ${existing.mode === 'leaveBy' ? '' : 'checked'} />
              To event start
            </label>
            <label>
              <input type="radio" name="countdown-mode" value="leaveBy" ${existing.mode === 'leaveBy' ? 'checked' : ''} />
              Leave before event
            </label>
          </div>

          <label for="countdown-offset">Lead time (minutes)</label>
          <input id="countdown-offset" type="number" min="0" max="300" value="${Number(existing.offsetMinutes || 0)}" />
        </div>
        <footer class="countdown-modal__footer">
          <button class="btn btn--ghost" data-action="close">Cancel</button>
          <button class="btn btn--primary" data-action="save">${countdown ? 'Update' : 'Save'} countdown</button>
        </footer>
      </div>
    `;

    document.body.appendChild(modal);

    const close = () => modal.remove();

    modal.querySelectorAll('[data-action="close"]').forEach((btn) => {
      btn.addEventListener('click', close);
    });

    const eventSelect = modal.querySelector('#countdown-event');
    const dateInput = modal.querySelector('#countdown-date');
    const timeInput = modal.querySelector('#countdown-time');

    const applyExistingDate = (targetIso) => {
      const date = targetIso ? new Date(targetIso) : new Date();
      if (!Number.isNaN(date.getTime())) {
        dateInput.value = date.toISOString().slice(0, 10);
        timeInput.value = date.toTimeString().slice(0, 5);
      }
    };

    applyExistingDate(existing.target);

    eventSelect?.addEventListener('change', () => {
      const selectedId = eventSelect.value;
      if (!selectedId) {
        return;
      }
      const event = calendarEvents.find((evt) => evt.id === selectedId);
      if (!event?.start) {
        return;
      }
      const eventDate = new Date(event.start);
      if (Number.isNaN(eventDate.getTime())) {
        return;
      }
      dateInput.value = eventDate.toISOString().slice(0, 10);
      timeInput.value = eventDate.toTimeString().slice(0, 5);
      const labelInput = modal.querySelector('#countdown-label');
      if (labelInput && (!labelInput.value || labelInput.value === existing.label)) {
        labelInput.value = event.summary || 'Event';
      }
    });

    modal.querySelector('[data-action="save"]')?.addEventListener('click', () => {
      const labelInput = modal.querySelector('#countdown-label');
      const typeSelect = modal.querySelector('#countdown-type');
      const mode = modal.querySelector('input[name="countdown-mode"]:checked')?.value || 'eventStart';
      const offsetMinutes = Math.max(0, Number(modal.querySelector('#countdown-offset')?.value || 0));

      const label = labelInput.value.trim();
      if (!label) {
        labelInput.focus();
        return;
      }

      const dateValue = dateInput.value;
      const timeValue = timeInput.value;
      if (!dateValue || !timeValue) {
        dateInput.focus();
        return;
      }

      const localDate = new Date(`${dateValue}T${timeValue}:00`);
      if (Number.isNaN(localDate.getTime())) {
        dateInput.focus();
        return;
      }

      const targetIso = localDate.toISOString();
      const calendarEventId = eventSelect ? eventSelect.value || null : null;
      const source = calendarEventId ? 'calendar' : 'manual';
      const category = typeSelect?.value === 'appointment' ? 'appointment' : 'custom';
      const updatedAt = new Date().toISOString();

      const customCountdown = {
        id: existing.id,
        label,
        category,
        type: category,
        target: targetIso,
        targetDateTime: targetIso,
        mode,
        offsetMinutes,
        source,
        calendarEventId,
        active: true,
        updatedAt,
        metadata: { ...(existing.metadata || {}), updatedAt },
      };

      this.saveCustomCountdown(customCountdown);

      close();
    });

    modal.querySelector('#countdown-label')?.focus();
  }

  statusForDiff(diffMs) {
    if (diffMs <= 0) return 'critical';
    if (diffMs <= 15 * 60 * 1000) return 'warning';
    if (diffMs <= 2 * 60 * 60 * 1000) return 'calm';
    return 'calm';
  }

  formatDiff(diffMs) {
    if (diffMs <= 0) return 'Now';
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainderHours = hours % 24;
      if (remainderHours === 0) return `${days} day${days === 1 ? '' : 's'}`;
      return `${days}d ${remainderHours}h`;
    }
    if (mins === 0) return `${hours} hr${hours === 1 ? '' : 's'}`;
    return `${hours}h ${mins}m`;
  }

  formatTimeLabel(date) {
    return date.toLocaleString([], {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  formatDiffFromNow(iso) {
    if (!iso) return '--';
    return this.formatDiff(new Date(iso).getTime() - Date.now());
  }

  generateId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `countdown_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeHtmlAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}
